import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchCategories } from "../store/slices/categorySlice";
import { fetchSearchResults, setSearchParams, updateFilters } from "../store/slices/searchSlice";
import Checkbox from "../components/inputs/checkbox";

const CategoryFilterOffcanvas = ({ isOpen, onClose, categoryId, categoryName }) => {
  const dispatch = useDispatch();
  const { categories, status, error } = useSelector((state) => state.categories);
  const searchState = useSelector((state) => state.search);

  // Helper to find a category by ID in the full hierarchy
  const findCategoryById = useCallback((id, categoriesList) => {
    const numId = Number(id);
    for (const level1 of categoriesList) {
      if (level1.id === numId) return level1;
      if (level1.children) {
        for (const level2 of level1.children) {
          if (level2.id === numId) return level2;
          if (level2.children) {
            for (const level3 of level2.children) {
              if (level3.id === numId) return level3;
            }
          }
        }
      }
    }
    return null;
  }, []);

  // Helper to get the top-level (Level 1) ancestor for a given categoryId
  const getTopLevelAncestor = useCallback((categoriesList, targetId) => {
    if (!targetId) {
     // console.log(`[getTopLevelAncestor] No targetId provided.`);
      return null;
    }
    const numTargetId = Number(targetId);
   // console.log(`[getTopLevelAncestor] Searching for targetId: ${numTargetId}`);

    for (const level1 of categoriesList) {
      // console.log(`[getTopLevelAncestor] Checking level1: ${level1.id} (${level1.name})`);
      if (level1.id === numTargetId) {
        // console.log(`[getTopLevelAncestor] Found: Target is level1: ${level1.name}`);
        return level1; // Target is a level 1 category
      }
      if (level1.children) {
        for (const level2 of level1.children) {
          if (level2.id === numTargetId) {
            // console.log(`[getTopLevelAncestor] Found: Target is level2 (${level2.name}), ancestor is level1: ${level1.name}`);
            return level1; // Target is a level 2 category, return its level 1 parent
          }
          if (level2.children) {
            for (const level3 of level2.children) {
              if (level3.id === numTargetId) {
                // console.log(`[getTopLevelAncestor] Found: Target is level3 (${level3.name}), ancestor is level1: ${level1.name}`);
                return level1; // Target is a level 3 category, return its level 1 grandparent
              }
            }
          }
        }
      }
    }
    // console.log(`[getTopLevelAncestor] TargetId ${numTargetId} not found as part of any level1 hierarchy.`);
    return null; // Target not found in any hierarchy
  }, []);


  const [selectedCategories, setSelectedCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchCategories());
    }
  }, [dispatch, status]);

  useEffect(() => {
    if (categories.length > 0) {
      // --- Part 1: Handle selectedCategories state (checkboxes) ---
      const newSelectedCategories = [];
      const newSelectedIds = new Set();

      // Initialize with categoryId from props if present
      const currentCategoryId = categoryId || searchState.categoryId; // Prefer prop, then Redux state
      if (currentCategoryId) {
        const numCatId = Number(currentCategoryId);
        const foundCategory = findCategoryById(numCatId, categories);
        if (foundCategory && !newSelectedIds.has(numCatId)) {
          newSelectedCategories.push({ id: foundCategory.id, name: foundCategory.name });
          newSelectedIds.add(numCatId);
        }
      }

      // Merge with searchState.filtre
      searchState.filtre?.forEach(filterIdStr => {
        const filterId = Number(filterIdStr);
        if (!isNaN(filterId) && !newSelectedIds.has(filterId)) {
          const foundFromFilter = findCategoryById(filterId, categories);
          if (foundFromFilter) {
            newSelectedCategories.push({ id: foundFromFilter.id, name: foundFromFilter.name });
            newSelectedIds.add(filterId);
          }
        }
      });
      // console.log({newSelectedCategories})
      setSelectedCategories(newSelectedCategories);
      // console.log("Calculated selectedCategories:", JSON.stringify(newSelectedCategories, null, 2));


      // --- Part 2: Handle filteredCategories state (what top-level branches to display) ---
      if (currentCategoryId) { // Use the determined currentCategoryId for filtering display
        const ancestor = getTopLevelAncestor(categories, currentCategoryId);
        // console.log(`Determined ancestor for filtering display:`, JSON.stringify(ancestor, null, 2));
        if (ancestor) {
          setFilteredCategories([ancestor]);
          // console.log("Setting filteredCategories to (ancestor branch):", JSON.stringify([ancestor], null, 2));

          // Auto-expand logic (ensure ancestor is used for path finding root)
          const path = [];
          const findPathToExpand = (currentCategory, targetId, currentPath) => {
            if (!currentCategory) return false;
            currentPath.push(currentCategory.id); // Push current node to path before checking children
            if (currentCategory.id === targetId) {
                path.push(...currentPath.filter(id => id !== targetId)); // Don't include target itself
                return true;
            }
            if (currentCategory.children) {
              for (const child of currentCategory.children) {
                if (findPathToExpand(child, targetId, [...currentPath])) { // Pass a copy of currentPath
                  return true;
                }
              }
            }
            return false;
          };

          const numCatId = Number(currentCategoryId);
          // Only try to find path if ancestor is not the categoryId itself, or if it is the ancestor and has children
          if (ancestor.id !== numCatId || (ancestor.id === numCatId && ancestor.children?.length > 0)) {
            findPathToExpand(ancestor, numCatId, []);
            // console.log("Path to expand for auto-expansion:", JSON.stringify(path, null, 2));
            setExpandedCategories(prev => [...new Set([...prev, ...path])]);
          }


        } else {
          // console.warn(`Top-level ancestor for category ID ${currentCategoryId} not found. Displaying ALL categories as fallback.`);
          setFilteredCategories(categories);
        }
      } else {
        // No categoryId provided, show all categories
        setFilteredCategories(categories);
        // console.log("Setting filteredCategories to (all categories - no categoryId):", JSON.stringify(categories, null, 2));
      }
    }
    // console.log("--- CategoryFilterOffcanvas: useEffect END ---");
  }, [
    categories,
    categoryId,
    categoryName,
    searchState.filtre,
    searchState.categoryId, // Added to dependency array
    findCategoryById,
    getTopLevelAncestor,
    status
  ]);


  // Helper to get all descendant IDs (including sub-children)
  const getAllChildIds = useCallback((category) => {
    let childIds = [];
    if (category.children) {
      category.children.forEach((child) => {
        childIds.push(child.id);
        childIds = [...childIds, ...getAllChildIds(child)];
      });
    }
    return childIds;
  }, []);

  const isIndeterminate = useCallback(
    (category) => {
      if (!category.children || category.children.length === 0) return false;
      const allDescendantIds = getAllChildIds(category);
      const selectedDescendantCount = selectedCategories.filter(cat =>
        allDescendantIds.includes(cat.id)
      ).length;
      const isCategoryItselfSelected = selectedCategories.some(cat => cat.id === category.id);
      return selectedDescendantCount > 0 && selectedDescendantCount < allDescendantIds.length && !isCategoryItselfSelected;
    },
    [selectedCategories, getAllChildIds]
  );

  const areAllChildrenSelected = useCallback(
    (category) => {
      const isCategoryItselfSelected = selectedCategories.some(cat => cat.id === category.id);
      if (!category.children || category.children.length === 0) {
        return isCategoryItselfSelected;
      }
      return isCategoryItselfSelected && category.children.every(child => areAllChildrenSelected(child));
    },
    [selectedCategories]
  );

  const countSelectedChildren = useCallback(
    (category) => {
      let count = 0;
      if (selectedCategories.some(cat => cat.id === category.id)) {
        count++;
      }
      if (category.children) {
        category.children.forEach(child => {
          count += countSelectedChildren(child);
        });
      }
      return count;
    },
    [selectedCategories]
  );

  
  const toggleCategory = (idToToggle, nameToToggle, categoryToToggle) => {
    let newSelectedCategories = [...selectedCategories];
    const relevantIds = new Set();
    relevantIds.add(idToToggle);
  
    // Get all child IDs if this is a parent category
    if (categoryToToggle?.children) {
      getAllChildIds(categoryToToggle).forEach(id => relevantIds.add(id));
    }
  
    // Check if the specific item being toggled is currently selected
    const isCurrentlySelected = newSelectedCategories.some(cat => cat.id === idToToggle);
  
    if (isCurrentlySelected) {
      // Remove the toggled item and all its children
      newSelectedCategories = newSelectedCategories.filter(
        (cat) => !relevantIds.has(cat.id)
      );
    } else {
      // First remove the parent if it exists in the selection
      if (categoryToToggle?.parent_id) {
        newSelectedCategories = newSelectedCategories.filter(
          cat => cat.id !== categoryToToggle.parent_id
        );
      }
  
      // Add the toggled item
      newSelectedCategories.push({ id: idToToggle, name: nameToToggle });
  
      // If this is a parent, add all children
      if (categoryToToggle?.children) {
        categoryToToggle.children.forEach(child => {
          if (!newSelectedCategories.some(cat => cat.id === child.id)) {
            newSelectedCategories.push({ id: child.id, name: child.name });
          }
        });
      }
    }
  
    // Remove duplicates
    const uniqueSelected = [];
    const seen = new Set();
    newSelectedCategories.forEach(cat => {
      if (!seen.has(cat.id)) {
        uniqueSelected.push(cat);
        seen.add(cat.id);
      }
    });
  
    setSelectedCategories(uniqueSelected);
  };

  const handleApplyFilters = () => {
    console.log({selectedCategories})
  
    const categoryIds = selectedCategories.map((cat) => cat.id);
    const categoryNames = selectedCategories.map((cat) => cat.name).join(", ");
    dispatch(updateFilters([...categoryIds]));
    const shouldNullifyCategoryId =
      categoryId &&
      searchState.categoryId === categoryId &&
      !categoryIds.includes(Number(categoryId));
    const searchData = {
      categoryId: shouldNullifyCategoryId ? null : searchState.categoryId,
      filtre: categoryIds,
      query: categoryNames || (shouldNullifyCategoryId ? "" : searchState.query),
      categoryName: categoryNames || (shouldNullifyCategoryId ? "" : searchState.categoryName),
      location: searchState.location,
      how: searchState.how || "onsite",
      when: searchState.when || "all_time",
      lat: searchState.lat || null,
      lng: searchState.lng || null,
      sortBy: searchState.sortBy || "relevance",
    };
    dispatch(setSearchParams(searchData));
    dispatch(fetchSearchResults(searchData));
    onClose();
  };

  const handleResetFilters = () => {
    setSelectedCategories([]);
    dispatch(updateFilters([]));
    const searchData = {
      ...searchState,
      filtre: [],
      categoryName: "",
      categoryId: null,
    };
    dispatch(setSearchParams(searchData));
    dispatch(fetchSearchResults(searchData));
    onClose();
  };

  const toggleExpand = (id) => {
    setExpandedCategories((prev) =>
      prev.includes(id)
        ? prev.filter((catId) => catId !== id)
        : [...prev, id]
    );
  };

  const renderCategory = (category, level = 0) => {
    const hasChildren = category.children?.length > 0;
    const isExpanded = expandedCategories.includes(category.id);
    const isCategoryItselfChecked = selectedCategories.some((cat) => cat.id === category.id);
    const allChildrenSel = areAllChildrenSelected(category);
    const indeterminate = isIndeterminate(category);
    let label = category.name;
    if (hasChildren) {
      const count = countSelectedChildren(category);
      if (count > 0) {
        label = `${category.name} (${count})`;
      }
    }

    if (level === 0) {
      return (
        <div key={category.id} className="category-item-filter">
          <h4>{category.name}</h4>
          {hasChildren && (
            <div className="subcategory-list" style={{ columnCount: 1, columnGap: "20px" }}>
              {category.children.map((child) => renderCategory(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={category.id} className="category-item-filter">
        <div className={`${hasChildren && 'twyny-border-bottom pb-16 mb-0'} category-header-filter`}>
          <Checkbox
            id={`category-${category.id}`}
            label={label}
            checked={isCategoryItselfChecked || allChildrenSel}
            indeterminate={indeterminate}
            onChange={() => toggleCategory(category.id, category.name, category)}
            className={hasChildren ? "bold500" : ''}
          />
          {hasChildren && (
            <span className="expand-icon" onClick={() => toggleExpand(category.id)}>
              <img
                src={isExpanded ? "/images/downarrow.svg" : "/images/rightarrow.svg"}
                alt={isExpanded ? "Collapse" : "Expand"}
                style={{ width: "16px", height: "16px" }}
              />
            </span>
          )}
        </div>
        {isExpanded && hasChildren && (
          <div className="subcategory-list" style={{ columnCount: 2, marginTop: "8px" }}>
            {category.children.map((child) => renderCategory(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (status === "loading" && categories.length === 0) return <div>Loading categories...</div>;
  if (status === "failed") return <div>Error loading categories: {error}</div>;

  return (
    <div className={`filter-search offcanvas offcanvas-end ${isOpen ? "show" : ""}`} tabIndex="-1">
      <div className="offcanvas-header">
        <h3 className="grey500 mb-0">Type de prestation</h3>
        <button onClick={onClose} className="close-button">
          <img src="/images/x.svg" alt="close" />
        </button>
      </div>
      <div className="offcanvas-body">
        <div className="category-list">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category) => renderCategory(category, 0))
          ) : (
            status !== "loading" && <div>No categories found or filter criteria not met.</div>
          )}
        </div>
        <div className="fixed-scroll">
          <div className="filter-actions d-flex justify-content-between ">
            <button onClick={handleResetFilters} className="twyny-cta-secondary">
              Annuler
            </button>
            <button onClick={handleApplyFilters} className="twyny-cta-primary">
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryFilterOffcanvas;